import { useState } from 'react';
import { App, Button, Flex, Input, Select, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { ImagePicker } from '@/shared/ui/ImagePicker';
import { INVALID_INPUTS_HINT, IntInput, InvalidInputsContext, MoneyInput, useInvalidInputs } from '@/shared/ui/inputs';
import { translateApiError } from '@/shared/api/errorToMessage';
import { formatDateTimeInTz } from '@/shared/lib/date';
import { useStoreContext } from '@/shared/lib/store-context';
import {
  CategoryImageField,
  useArchiveCategoryMutation,
  useCategoryPriceHistoryQuery,
  useCreateCategoryMutation,
  useListCategoriesQuery,
  useSetWholesalePriceMutation,
  useUnarchiveCategoryMutation,
  useUpdateCategoryMutation,
  useUploadPendingImages,
} from '@/entities/category';
import type { CategoryPriceHistoryEntry } from '@/entities/category';
import { priceSourceLabel } from './model';

export interface CategoryWindowProps {
  id?: string;
  parentId?: string;
  onClose: () => void;
}

/** §2.1: окно категории — корневая группа (без N) или основная категория (с N). */
export function CategoryWindow({ id, parentId, onClose }: CategoryWindowProps) {
  const { message } = App.useApp();
  const ctx = useStoreContext();
  const { data: categories = [] } = useListCategoriesQuery({ includeArchived: true });
  const existing = categories.find((c) => c.id === id);
  const roots = categories.filter((c) => c.parentId === null);
  const isMainExisting = !!id && !!existing?.parentId;
  const { data: priceHistory = [] } = useCategoryPriceHistoryQuery(id ?? '', { skip: !isMainExisting });

  const [name, setName] = useState('');
  const [selectedParentId, setSelectedParentId] = useState<string | undefined>(parentId);
  const [pcsPerBox, setPcsPerBox] = useState<number | null>(null);
  const [wholesalePrice, setWholesalePrice] = useState<string | null>(null);
  // Фото новой категории: файл ждёт, пока запись создастся (нужен её id). У существующей фото
  // правится сразу через `CategoryImageField`.
  const [photo, setPhoto] = useState<File | null>(null);
  const uploadPendingImages = useUploadPendingImages();
  const [seededId, setSeededId] = useState<string | undefined>(undefined);
  if (existing && existing.id !== seededId) {
    setSeededId(existing.id);
    setName(existing.name);
    setSelectedParentId(existing.parentId ?? undefined);
    setPcsPerBox(existing.pcsPerBox);
    setWholesalePrice(existing.wholesalePrice);
  }

  const [createCategory, { isLoading: isCreating }] = useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateCategoryMutation();
  const [setWholesale] = useSetWholesalePriceMutation();
  const [archive] = useArchiveCategoryMutation();
  const [unarchive] = useUnarchiveCategoryMutation();

  const isMain = selectedParentId !== undefined;
  const pcsLocked = Boolean(existing?.hasPostedDocuments);
  const busy = isCreating || isUpdating;
  // У товара N обязателен: пустое или неверное поле («0», «1,5») сохранять нельзя — после первого
  // проведённого документа N уже не исправить.
  const pcsMissing = isMain && !pcsLocked && pcsPerBox === null;
  // Цена с опечаткой («14о», «-5») уходит из поля как «пусто» — окно сохранилось бы, не изменив
  // цену и ничего об этом не сказав.
  const invalidInputs = useInvalidInputs();

  async function handleSave() {
    if (invalidInputs.hasInvalid) return;
    try {
      if (!id) {
        const created = await createCategory({ name, parentId: selectedParentId, pcsPerBox: isMain ? (pcsPerBox ?? undefined) : undefined }).unwrap();
        if (photo) {
          const failed = await uploadPendingImages([{ id: created.id, name, file: photo }]);
          if (failed.length > 0) message.warning('Фото не загрузилось. Категория создана — добавьте фото позже в её окне');
        }
      } else {
        await updateCategory({ id, body: { name, pcsPerBox: isMain && !pcsLocked ? (pcsPerBox ?? undefined) : undefined } }).unwrap();
        if (isMain && wholesalePrice !== existing?.wholesalePrice && wholesalePrice) {
          await setWholesale({ id, price: wholesalePrice }).unwrap();
        }
      }
      message.success('Сохранено');
      onClose();
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  return (
    <FloatingWindow
      kind="category"
      title={id ? `${existing && existing.parentId === null ? 'Группа' : 'Категория'}: ${existing?.name ?? ''}` : selectedParentId ? 'Новая категория' : 'Новая группа'}
      onClose={onClose}
      footer={
        <Space>
          <Button type="primary" loading={busy} onClick={() => void handleSave()} disabled={!name || pcsMissing || invalidInputs.hasInvalid}>
            Сохранить
          </Button>
          {id && existing ? (
            existing.isArchived ? (
              <Button onClick={() => void unarchive(id)}>Вернуть из архива</Button>
            ) : (
              <Button onClick={() => void archive(id)}>В архив</Button>
            )
          ) : null}
          <Button onClick={onClose}>Закрыть</Button>
        </Space>
      }
    >
      <InvalidInputsContext.Provider value={invalidInputs.api}>
      <Flex vertical gap={12} style={{ maxWidth: 420 }}>
        <Flex vertical gap={4}>
          <Typography.Text>Название</Typography.Text>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Flex>

        <Flex vertical gap={4}>
          <Typography.Text>Фото (необязательно)</Typography.Text>
          {id ? (
            existing ? <CategoryImageField category={existing} /> : null
          ) : (
            <Flex gap={8} align="center">
              <ImagePicker file={photo} onChange={setPhoto} onReject={(reason) => void message.warning(reason)} title="Выбрать фото" />
              <Typography.Text type="secondary">JPG, PNG или WebP, до 10 МБ. Можно добавить позже.</Typography.Text>
            </Flex>
          )}
        </Flex>

        {!id ? (
          <Flex vertical gap={4}>
            <Typography.Text>Группа (пусто — новая корневая группа)</Typography.Text>
            <Select
              allowClear
              value={selectedParentId}
              onChange={(v) => setSelectedParentId(v)}
              options={roots.map((r) => ({ value: r.id, label: r.name }))}
            />
          </Flex>
        ) : null}

        {isMain ? (
          <>
            <Flex vertical gap={4}>
              <Typography.Text>Штук в коробке (N)</Typography.Text>
              <IntInput value={pcsPerBox} onChange={setPcsPerBox} disabled={pcsLocked} />
              {pcsLocked ? <Typography.Text type="secondary">По категории есть проведённые документы</Typography.Text> : null}
              {pcsMissing ? <Typography.Text type="secondary">Целое число от 1 — без него товар не сохранить</Typography.Text> : null}
            </Flex>
            {id ? (
              <Flex vertical gap={4}>
                <Typography.Text>Оптовая цена</Typography.Text>
                <MoneyInput value={wholesalePrice} onChange={setWholesalePrice} />
              </Flex>
            ) : null}
            {id && priceHistory.length > 0 ? (
              <Flex vertical gap={4}>
                <Typography.Text>История цены</Typography.Text>
                <Table<CategoryPriceHistoryEntry>
                  size="small"
                  rowKey="id"
                  dataSource={priceHistory}
                  columns={priceHistoryColumns(ctx.timezone)}
                  pagination={false}
                />
              </Flex>
            ) : null}
          </>
        ) : null}
        {invalidInputs.hasInvalid ? <Typography.Text type="danger">{INVALID_INPUTS_HINT}</Typography.Text> : null}
      </Flex>
      </InvalidInputsContext.Provider>
    </FloatingWindow>
  );
}

/** §2.1 [ПРЕДЛ]: «История цены» — сырые записи `AuditLog` за `WHOLESALE_PRICE_CHANGED`. */
function priceHistoryColumns(timezone: string): ColumnsType<CategoryPriceHistoryEntry> {
  return [
    { title: 'Дата', width: 130, render: (_v, r) => formatDateTimeInTz(r.createdAt, timezone) },
    { title: 'Было', width: 90, align: 'right', render: (_v, r) => r.before.wholesalePrice ?? '—' },
    { title: 'Стало', width: 90, align: 'right', render: (_v, r) => r.after.wholesalePrice ?? '—' },
    {
      title: 'Источник',
      render: (_v, r) => priceSourceLabel(r.after),
    },
  ];
}
